import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  SkipNext,
  RestartAlt,
} from '@mui/icons-material';
import { TourStep } from 'src/types/tutorial';

interface InteractiveTourProps {
  open: boolean;
  steps: TourStep[];
  tourId: string;
  onClose: () => void;
  onComplete?: () => void;
  onSkip?: () => void;
}

const InteractiveTour: React.FC<InteractiveTourProps> = ({
  open,
  steps,
  tourId,
  onClose,
  onComplete,
  onSkip,
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && steps.length > 0) {
      setCurrentStep(0);
      highlightStep(0);
    }
  }, [open, steps]);

  useEffect(() => {
    if (open) {
      highlightStep(currentStep);
    }
    // Cleanup on unmount or close
    return () => {
      if (highlightedElement) {
        highlightedElement.style.outline = '';
        highlightedElement.style.outlineOffset = '';
        highlightedElement.style.zIndex = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' && currentStep < steps.length - 1) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentStep, steps.length]);

  const highlightStep = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (!step) return;

    // Remove previous highlight
    if (highlightedElement) {
      highlightedElement.style.outline = '';
      highlightedElement.style.outlineOffset = '';
      highlightedElement.style.zIndex = '';
      setHighlightedElement(null);
    }

    // Wait a bit for DOM to be ready, then find target element
    setTimeout(() => {
      const targetElement = document.querySelector(step.target) as HTMLElement;
      if (targetElement) {
        setHighlightedElement(targetElement);
        targetElement.style.outline = `3px solid ${theme.palette.primary.main}`;
        targetElement.style.outlineOffset = '4px';
        targetElement.style.zIndex = '9998';
        
        // Scroll into view
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Clean up highlights
    if (highlightedElement) {
      highlightedElement.style.outline = '';
      highlightedElement.style.outlineOffset = '';
      highlightedElement.style.zIndex = '';
    }
    onSkip?.();
    onClose();
  };

  const handleComplete = () => {
    // Clean up highlights
    if (highlightedElement) {
      highlightedElement.style.outline = '';
      highlightedElement.style.outlineOffset = '';
      highlightedElement.style.zIndex = '';
    }
    onComplete?.();
    onClose();
  };

  const handleRestart = () => {
    setCurrentStep(0);
  };

  if (!open || steps.length === 0) return null;

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <>
      {/* Overlay */}
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9997,
          pointerEvents: 'auto',
        }}
        onClick={(e) => {
          // Don't close on overlay click - require explicit skip/close
          e.stopPropagation();
        }}
      />

      {/* Tour Dialog */}
      <Dialog
        open={open}
        onClose={handleSkip}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            zIndex: 9999,
            m: 2,
          },
        }}
        disableEscapeKeyDown={false}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="div">
            Interactive Tour
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Step {currentStep + 1} of {steps.length}
            </Typography>
            <IconButton size="small" onClick={handleSkip} aria-label="Close">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Progress Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 3, mt: 1 }}>
            {steps.map((step, index) => (
              <Step key={index} completed={index < currentStep}>
                <StepLabel />
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Paper
            ref={stepContentRef}
            elevation={0}
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              borderRadius: 2,
              mb: 2,
            }}
          >
            {currentStepData.title && (
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {currentStepData.title}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              {typeof currentStepData.content === 'string'
                ? currentStepData.content
                : currentStepData.content}
            </Typography>
          </Paper>

          {/* Keyboard Shortcuts Hint */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            💡 Tip: Use arrow keys to navigate, Esc to skip
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Box>
            {!isFirstStep && (
              <Button
                startIcon={<RestartAlt />}
                onClick={handleRestart}
                size="small"
                sx={{ mr: 1 }}
              >
                Restart
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleSkip} color="inherit" startIcon={<SkipNext />}>
              Skip Tour
            </Button>
            {!isFirstStep && (
              <Button
                onClick={handlePrevious}
                startIcon={<ArrowBack />}
                variant="outlined"
              >
                Previous
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="contained"
              endIcon={isLastStep ? undefined : <ArrowForward />}
            >
              {isLastStep ? 'Complete' : 'Next'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InteractiveTour;

